import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';

export const useIsVisibleInViewport = <T extends Element>(options?: {
  /**
   * number between 0 and 1, default is 0. 1 means the element must be fully visible.
   */
  threshold?: number;
  callback?: () => void;
  alwaysOnCallback?: (isVisible: boolean) => void;
  skip?: boolean;
}) => {
  const [node, setNode] = useState<T>();
  const [isVisible, setVisible] = useState<boolean>();
  const [hasBeenVisible, setHasBeenVisible] = useState<boolean>(false);
  const isVisibleRef = useRef(false);

  const handleRef = useCallback(
    (el: T | null) => {
      if (el) {
        setNode(el);
      }
    },
    [setNode]
  );

  const {
    threshold = 0,
    callback,
    alwaysOnCallback,
    skip = false,
  } = options || {};

  useEffect(() => {
    if (!skip && node && typeof IntersectionObserver !== 'undefined') {
      const observer = new IntersectionObserver(
        ([entry]) => {
          setVisible(entry.isIntersecting);

          if (entry.isIntersecting && !hasBeenVisible) {
            setHasBeenVisible(true);
          }
          alwaysOnCallback?.(entry.isIntersecting);
          if (entry.isIntersecting && !isVisibleRef.current) {
            callback?.();
          }
        },
        {
          threshold,
        }
      );

      observer.observe(node);

      return () => observer.unobserve(node);
    }
    return () => null;
  }, [node, callback, threshold, alwaysOnCallback, hasBeenVisible, skip]);

  useLayoutEffect(() => {
    if (!skip && !isVisibleRef.current && isVisible) {
      isVisibleRef.current = true;
    }
  }, [isVisible, skip]);

  return {
    handleRef,
    node,
    isVisible,
    hasBeenVisible,
  };
};
